import React, { ReactElement, ReactNode } from 'react';
import { Actions, MultiInputControls } from 'pages/components/form/MultiInput/MultiInputControls';
import { IntlShape, useIntl } from 'react-intl';
import { getDefaultWorkRelatedIdentifierExternal } from '../../../../Settings/emptyModels';
import { ValidationResult } from '../../../../util/validation/interfaces';
import { getVocabularyOptions } from '../../../../Vocabulary/util';
import { CheckPermissionFunction } from '../../../../Auth/components/PermissionGate';
import { InputGroup } from '../layout/InputGroup';
import { LabelledDropdown } from '../../../components/form/LabelledDropdown';
import { Panel } from '../layout/Panel';
import { InputText } from 'primereact/inputtext';
import { WorkRelatedIdentifierDTO } from '../../../../api/core';
import { LabelledInputText } from '../../../components/form/LabelledInputText';

interface WorkRelatedIdentifierExternalProps {
  title: string;
  identifiers: WorkRelatedIdentifierDTO[];
  onChange: (subject: WorkRelatedIdentifierDTO[]) => void;
  pubtypeGroup: string;
  validation: ValidationResult;
  checkPermission: CheckPermissionFunction;
  autoFocus: boolean;
}

export function WorkRelatedIdentifierExternal(props: WorkRelatedIdentifierExternalProps): ReactNode {
  const intl: IntlShape = useIntl();
  return (
    <Panel title={props.title} toggleable={true}>
      <MultiInputControls<WorkRelatedIdentifierDTO>
        lines={props.identifiers || []}
        onChange={props.onChange}
        addNew={(rows: WorkRelatedIdentifierDTO[]): number => rows.push(getDefaultWorkRelatedIdentifierExternal())}
        canEdit={props.checkPermission().canWrite}
        renderLines={(
          identifier: WorkRelatedIdentifierDTO,
          onLineChange: (workRelatedIdentifierDTO: WorkRelatedIdentifierDTO) => void,
          actions: Actions,
          idx: number
        ): ReactElement => (
          <InputGroup
            delete={{
              action: actions.delete,
              disabled: !props.checkPermission().canWrite,
            }}
          >
            <LabelledInputText
              id={`internalRelation-${idx}-target`}
              autoFocus={props.autoFocus}
              className={'w-full'}
              wrapperClassName={'flex-1 min-w-0'}
              value={identifier.relatedIdentifier}
              permissions={props.checkPermission('related_identifier')}
              validation={props.validation?.children[idx]?.fields?.related_identifier}
              onChange={(relatedIdentifier: string): void => onLineChange({ ...identifier, relatedIdentifier })}
              label={`${props.title}.attr.referencedWork`}
            />
            <LabelledDropdown
              id={`internalRelation-${idx}-type`}
              value={identifier.relationType}
              className={'w-full'}
              wrapperClassName={'flex-1 min-w-0'}
              permissions={props.checkPermission('relation_type')}
              validation={props.validation?.children[idx]?.fields?.relation_type}
              onChange={(relationType: string): void => onLineChange({ ...identifier, relationType })}
              options={getVocabularyOptions(
                intl.locale,
                'relation_type_list',
                props.pubtypeGroup === 'RESEARCH_DATA_GROUP' ? 'research_data_group_external' : 'generic_external'
              )}
              label={`${props.title}.attr.type`}
            />
            <InputText
              id={`internalRelation-${idx}-source`}
              value={intl.formatMessage({ id: `${props.title}.attr.thisWork` })}
              disabled={true}
              className={'w-full flex-1 min-w-0'}
            />
          </InputGroup>
        )}
      />
    </Panel>
  );
}
